import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SideLayout from '../layouts/SideLayout';
import HeaderTitle from '../components/forecast/HeaderTitle';
import KpxSubmission from '../components/forecast/KpxSubmission';
import Generation from '../components/forecast/Generation';
import Dropdown from '../components/forecast/Dropdown';
import DateSelector from '../components/forecast/DateSelector';
import SearchButton from '../components/forecast/SearchButton';
import ForecastMeasurementSummary from '../components/forecast/ForecastMeasurementSummary';
import ForecastLineChart, {
  ForecastAreaMeasurementType,
  ForecastMeasurementType,
} from '../components/forecast/ForecastLineChart';
import ForecastDownloadButton from '../components/forecast/ForecastDownloadButton';
import { useTranslation } from 'react-i18next';
import { findKeyByValue } from '../utils/util';
import SVGImage from '../components/layout/SVGImage';
import { FetchDataArg, get, post } from '../utils/fetch';
import { useAppDispatch } from '../hooks/useRedux';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { cuidListResetState, fetchData } from '../slices/cuidListSlice';
import {
  fetchForecastCurrentMonthData,
  fetchForecastData,
  fetchForecastPeriodData,
  fetchForecastTodayData,
  fetchForecastTommorowData,
  resetCurrentMonthState,
  resetPeriodState,
  resetState,
  resetTodayState,
  resetTomorrowState,
} from '../slices/forecastSlice';
import { useNavigate } from 'react-router';
import { debounce } from 'lodash';
import {
  addDays,
  format,
  getQuarter,
  isAfter,
  isBefore,
  isSameDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { dataGenerator } from '../utils/data';
import { KpxSubmissionStatusType } from '../components/forecast/KpxSubmissionStatus';
import { DropdownSpinner } from '../components/forecast/DropdownSpinner';
import ForecastMultiSelectDropdown from '../components/forecast/ForecastMultiSelectDropdown';
import { ForecastPhaseSummary } from '../components/forecast/ForecastPhaseSummary';
import { ForecastSummaryCard } from '../components/forecast/ForecastSummaryCard';
import { ForecastAlert } from '../components/forecast/ForecastAlert';
import ForecastCuidDropdown from '@/components/forecast/ForecastCuidDropdown';

export enum ForecastPageChartType {
  ForecastChart = 'forecastChart',
  ErrorRateChart = 'errorRateChart',
  IncomRateChart = 'incomeRateChart',
}

export enum TimePeriod {
  Hourly = 'hourly',
  Daily = 'daily',
  Monthly = 'monthly',
  Yearly = 'yearly',
}

function ForecastPage() {
  // Local States
  const { t } = useTranslation();
  const [navigating, setNavigating] = useState(false);
  const [timePeriod, setTimePeriod] = useState(TimePeriod.Hourly);
  const [showForecastAlert, setShowForecastAlert] = useState(false);
  const [forecastEntityParams, setForecastEntityParams] =
    useState<ForecastEntityParams>({
      selectedCuidIdx: 0,
      selectedSiteIdIdx: 0,
      startDate: startOfMonth(new Date()),
      endDate: new Date(),
      monthlyYearStart: format(new Date(), 'yyyy') + t('forecast.year'),
      monthlyYearEnd: format(new Date(), 'yyyy') + t('forecast.year'),
      monthlyStart: '01' + t('forecast.month'),
      monthlyEnd: format(new Date(), 'MM') + t('forecast.month'),
      yearlyStart:
        format(subMonths(new Date(), 24), 'yyyy') + t('forecast.year'),
      yearlyEnd: format(new Date(), 'yyyy') + t('forecast.year'),
    });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    cuidList: {
      data: cuidListData,
      loading: cuidListLoading,
      error: cuidListError,
    },
    forecast: {
      data: forecastFetchedData,
      loading: forecastLoading,
      error: forecastError,
    },
    period: {
      data: periodFetchedData,
      loading: periodLoading,
      error: periodError,
    },
    today: { data: todayFetchedData, loading: todayLoading, error: todayError },
    tommorow: {
      data: tommorowFetchedData,
      loading: tommorowLoading,
      error: tommorowError,
    },
    currentMonth: {
      data: currentMonthFetchedData,
      loading: currentMonthLoading,
      error: currentMonthError,
    },
  } = useSelector((state: RootState) => state);
  const [isItemsChecked, setIsItemsChecked] = useState<Array<boolean>>([]);

  const cuidList = useMemo(
    () =>
      Object.values(cuidListData?.data ?? {}).map((cuid) => {
        return {
          cuid: cuid.cuid,
          cuidName: cuid.cuidName,
        };
      }),
    [cuidListData]
  );

  const siteIds = useMemo(() => {
    const cuid = cuidList[forecastEntityParams.selectedCuidIdx];
    if (cuid) {
      const selectedCuid = cuidListData?.data[cuid.cuid];
      setIsItemsChecked(
        Array.from({ length: selectedCuid?.sites?.length ?? 0 }).map(
          (_, index) => (index === 0 ? true : false)
        )
      );
      return selectedCuid?.sites ?? [];
    }
    return [];
  }, [cuidList, forecastEntityParams.selectedCuidIdx]);

  const selectedSiteIds = useMemo(() => {
    return siteIds
      .filter((_, index) => isItemsChecked[index])
      .map((site) => site.siteId);
  }, [siteIds, isItemsChecked]);

  // UseEffects
  useEffect(() => {
    setCheckboxesIsChecked({
      [ForecastPageChartType.ForecastChart]: !forecastChartDisabled,
      [ForecastPageChartType.ErrorRateChart]: !errorRateChartDisabled,
      [ForecastPageChartType.IncomRateChart]: !incomeRateChartDisabled,
    });
  }, [forecastFetchedData]);

  useEffect(() => {
    if (
      !forecastLoading &&
      !periodLoading &&
      !todayLoading &&
      !tommorowLoading &&
      !cuidListLoading &&
      !currentMonthLoading &&
      (forecastError?.code === 'A0001' ||
        periodError?.code === 'A0001' ||
        todayError?.code === 'A0001' ||
        tommorowError?.code === 'A0001' ||
        currentMonthError?.code === 'A0001' ||
        cuidListError?.code === 'A0001')
    ) {
      if (!navigating) {
        setNavigating(true);
        dispatch(resetState());
        dispatch(resetPeriodState());
        dispatch(cuidListResetState());
        dispatch(resetTodayState());
        dispatch(resetTomorrowState());
        dispatch(resetCurrentMonthState());
        navigate('/');
        setNavigating(false);
      }
    }
  }, [
    forecastLoading,
    forecastError,
    periodLoading,
    periodError,
    todayLoading,
    todayError,
    tommorowLoading,
    tommorowError,
    cuidListLoading,
    cuidListError,
    currentMonthError,
    currentMonthLoading,
  ]);

  useEffect(() => {
    // fetch cuid list when mount
    debouncedFetchData({ endpoint: '/customer/map', fetch: get });
  }, []);

  useEffect(() => {
    fetchForecastDataWrapper();
  }, [selectedSiteIds, timePeriod]);

  // Forecast Day Ahead Related Methods
  const forecastDayAheadDiffData = useMemo(() => {
    if (
      !todayFetchedData ||
      !tommorowFetchedData ||
      todayFetchedData.data.length === 0 ||
      tommorowFetchedData.data.length === 0
    )
      return {
        submissionFirst: undefined,
        submissionSecond: undefined,
        optimeValid: '',
        optimeValidDiff: 0,
        powerPred: '',
        powerPredDiff: 0,
      };

    const today = todayFetchedData?.data[0];
    const tommorow = tommorowFetchedData?.data[0];
    const todayOptimeValid = parseFloat(
      today.validOpHour ?? today.validOpHour ?? '0'
    ).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    const tommorowOptimeValid = parseFloat(
      tommorow.validOpHour ?? tommorow.validOpHour ?? '0'
    ).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    const tommorowPowerPred = parseFloat(
      tommorow.powerPredSecond ?? tommorow.powerPredFirst ?? '0'
    ).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    const optimeValidDiff =
      !tommorowOptimeValid || !todayOptimeValid
        ? 0
        : parseFloat(tommorowOptimeValid) - parseFloat(todayOptimeValid);
    const powerPredDiff =
      !tommorowPowerPred || !today.powerMetered
        ? 0
        : parseFloat(tommorowPowerPred) - parseFloat(today.powerMetered);

    return {
      optimeValidDiff: optimeValidDiff,
      powerPredDiff: powerPredDiff,
    };
  }, [todayFetchedData, tommorowFetchedData]);

  const forecastDayAheadData = useMemo(() => {
    if (!tommorowFetchedData || tommorowFetchedData.data.length === 0) {
      return {
        optimeValid: '',
        powerPred: '',
        submissionFirst: undefined,
        submissionSecond: undefined,
      };
    }
    const tommorow = tommorowFetchedData?.data[0];

    const tommorowOptimeValid = parseFloat(
      tommorow.validOpHour ?? tommorow.validOpHour ?? '0'
    ).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

    const tommorowPowerPred = parseFloat(
      tommorow.powerPredSecond ?? tommorow.powerPredFirst ?? '0'
    ).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

    return {
      submissionFirst: tommorow?.isSubmissionFirst,
      submissionSecond: tommorow?.isSubmissionSecond,
      optimeValid: tommorowOptimeValid,
      powerPred: tommorowPowerPred,
    };
  }, [todayFetchedData, tommorowFetchedData]);

  const forecastPeriodData = useMemo(() => {
    if (!periodFetchedData)
      return {
        errorRate: null,
        startYm: null,
        endYm: null,
      };

    return {
      errorRate: periodFetchedData.errorRate ?? null,
      startYm: periodFetchedData.startYm ?? null,
      endYm: periodFetchedData.endYm ?? null,
    };
  }, [periodFetchedData]);

  // Forecast Chart Related Methods
  const forecastData = useMemo(() => {
    return (
      forecastFetchedData?.data.map((data) => ({
        name: data.sk,
        [ForecastMeasurementType.FirstPrediction]:
          data.powerPredFirst != null ? parseFloat(data.powerPredFirst) : null,
        [ForecastMeasurementType.SecondPrediction]:
          data.powerPredSecond != null
            ? parseFloat(data.powerPredSecond)
            : null,
        [ForecastMeasurementType.Measurement]:
          data.powerMetered != null ? parseFloat(data.powerMetered) : null,
      })) ?? []
    );
  }, [forecastFetchedData, timePeriod]);

  const forecastChartDisabled = useMemo(
    () => !forecastData?.length,
    [forecastData]
  );

  // ErrorRate Chart Related Methods
  const errorRateData = useMemo(() => {
    return (
      forecastFetchedData?.data.map((data) => ({
        name: data.sk,
        [ForecastMeasurementType.PredictionErrorRate]: data.errAvg
          ? parseFloat(data.errAvg)
          : null,
        [ForecastAreaMeasurementType.FirstSettlementUnitPrice]: 4,
        [ForecastAreaMeasurementType.SecondSettlementUnitPrice]: 6,
      })) ?? []
    );
  }, [forecastFetchedData, timePeriod]);

  const errorRateChartDisabled = useMemo(
    () => !errorRateData?.length,
    [errorRateData]
  );

  // IncomeRate Chart Related Methods
  const incomeRateData = useMemo(() => {
    return (
      (forecastFetchedData?.data as DailyEntities)?.map((data) => ({
        name: data.sk,
        [ForecastMeasurementType.SettlementUnitPrice]: parseFloat(
          data.income ?? '0'
        ),
      })) ?? []
    );
  }, [forecastFetchedData, timePeriod]);

  const incomeRateChartDisabled = useMemo(
    () => !incomeRateData.length,
    [incomeRateData]
  );

  // Fetching Related Methods
  const debouncedFetchForecastData = useCallback(
    debounce((params: FetchDataArg) => {
      dispatch(fetchForecastData(params));
    }, 1000),
    [dispatch]
  );

  const debouncedFetchData = useCallback(
    debounce((params) => {
      dispatch(fetchData(params));
    }, 1000),
    [dispatch]
  );

  const debouncedFetchPeriodData = useCallback(
    debounce((params) => {
      dispatch(fetchForecastPeriodData(params));
    }, 1000),
    [dispatch]
  );

  const debouncedFetchTodayData = useCallback(
    debounce((params) => {
      dispatch(fetchForecastTodayData(params));
    }, 500),
    [dispatch]
  );

  const debouncedFetchTommorowData = useCallback(
    debounce((params) => {
      dispatch(fetchForecastTommorowData(params));
    }, 500),
    [dispatch]
  );

  const debouncedFetchCurrentMonthData = useCallback(
    debounce((params) => {
      dispatch(fetchForecastCurrentMonthData(params));
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    const cuid = cuidList[forecastEntityParams.selectedCuidIdx]?.cuid;

    // fetch day ahead data when mount
    if (cuid) {
      const now = new Date();
      const today = format(now, 'yyyy-MM-dd');
      const tommorow = format(addDays(now, 1), 'yyyy-MM-dd');
      const currentMonth = format(now, 'yyyy-MM');

      debouncedFetchTodayData({
        endpoint: '/forecast/daily',
        fetch: post,
        options: {
          body: JSON.stringify({
            startDate: today,
            endDate: today,
            siteIds: selectedSiteIds,
            cuid,
          }),
        },
      });
      debouncedFetchTommorowData({
        endpoint: '/forecast/daily',
        fetch: post,
        options: {
          body: JSON.stringify({
            startDate: tommorow,
            endDate: tommorow,
            siteIds: selectedSiteIds,
            cuid,
          }),
        },
      });
      debouncedFetchCurrentMonthData({
        endpoint: '/forecast/monthly',
        fetch: post,
        options: {
          body: JSON.stringify({
            startYm: currentMonth,
            endYm: currentMonth,
            siteIds: selectedSiteIds,
            cuid,
          }),
        },
      });
    }
  }, [
    selectedSiteIds,
    forecastEntityParams.selectedCuidIdx,
    cuidList,
    debouncedFetchTodayData,
    debouncedFetchTommorowData,
    debouncedFetchCurrentMonthData,
  ]);

  // DateSelector Related Methods
  const forecastHourlyAndDailyDateOnChange = useCallback(
    (key: string) => (date: Date | null) => {
      if (date) {
        switch (key) {
          case 'startDate':
            if (
              isBefore(date, forecastEntityParams.endDate) ||
              isSameDay(date, forecastEntityParams.endDate)
            )
              setForecastEntityParams({
                ...forecastEntityParams,
                [key]: date,
              });
            break;
          case 'endDate':
            if (
              isAfter(date, forecastEntityParams.startDate) ||
              isSameDay(date, forecastEntityParams.startDate)
            )
              setForecastEntityParams({
                ...forecastEntityParams,
                [key]: date,
              });
            break;
        }
      }
    },
    [forecastEntityParams]
  );

  const stringIsBefore = (
    date: string,
    compareDate: string,
    delimeter: string
  ): boolean => {
    return (
      parseInt(date.split(delimeter)[0]) <=
      parseInt(compareDate.split(delimeter)[0])
    );
  };

  const stringIsAfter = (
    date: string,
    compareDate: string,
    delimeter: string
  ): boolean => {
    return (
      parseInt(date.split(delimeter)[0]) >=
      parseInt(compareDate.split(delimeter)[0])
    );
  };

  const forecastYearlyDateOnChange = useCallback(
    (key: string) => (date: string) => {
      if (date) {
        switch (key) {
          case 'yearlyStart':
            if (
              stringIsBefore(
                date,
                forecastEntityParams.yearlyEnd,
                t('forecast.year')
              )
            )
              setForecastEntityParams({
                ...forecastEntityParams,
                [key]: date,
              });
            break;
          case 'yearlyEnd':
            if (
              stringIsAfter(
                date,
                forecastEntityParams.yearlyStart,
                t('forecast.year')
              )
            )
              setForecastEntityParams({
                ...forecastEntityParams,
                [key]: date,
              });
            break;
        }
      }
    },
    [forecastEntityParams]
  );
  const forecastMonthlyDateOnChange = useCallback(
    (key: string) => (date: string) => {
      if (date) {
        switch (key) {
          case 'monthlyYearStart':
          case 'monthlyYearEnd':
            setForecastEntityParams({
              ...forecastEntityParams,
              [key]: date,
            });
            break;
          case 'monthlyStart':
            if (
              stringIsBefore(
                date,
                forecastEntityParams.monthlyEnd,
                t('forecast.month')
              )
            )
              setForecastEntityParams({
                ...forecastEntityParams,
                [key]: date,
              });
            break;
          case 'monthlyEnd':
            if (
              stringIsAfter(
                date,
                forecastEntityParams.monthlyStart,
                t('forecast.month')
              )
            )
              setForecastEntityParams({
                ...forecastEntityParams,
                [key]: date,
              });
            break;
        }
      }
    },
    [forecastEntityParams]
  );

  const onSelected = (text: string) => {
    const timePeriod = findKeyByValue(
      t('forecast.timePeriod', { returnObjects: true }),
      text
    );
    setTimePeriod(timePeriod as TimePeriod);
  };
  const years = useMemo(
    () =>
      Array.from(
        { length: 26 },
        (_, i) => (2000 + i).toString() + t('forecast.year')
      ).sort((a, b) => {
        const yearA = parseInt(a, 10); // Extract the year number from the string
        const yearB = parseInt(b, 10);
        return yearB - yearA; // Compare in descending order
      }),
    []
  );
  const months = useMemo(
    () =>
      Array.from(
        { length: 12 },
        (_, i) => String(i + 1).padStart(2, '0') + t('forecast.month')
      ),
    []
  );
  const quarterData = useMemo(() => {
    const now = new Date();
    const quarter = getQuarter(now);
    const quarterMonths: { [key: number]: { start: string; end: string } } = {
      1: { start: '01', end: '03' },
      2: { start: '04', end: '06' },
      3: { start: '07', end: '09' },
      4: { start: '10', end: '12' },
    };

    // Get the start and end months of the current quarter
    const { start, end } = quarterMonths[quarter];

    // Format year, current date, and hour
    const year = format(now, 'yyyy');
    const currentDate = format(now, 'yyyy.MM.dd');
    const currentHour = format(now, 'HH');

    return {
      startDate: `${year}.${start}`,
      endDate: `${year}.${end}`,
      currentDate: currentDate,
      currentHour: currentHour,
    };
  }, []);

  useEffect(() => {
    const cuid = cuidList[forecastEntityParams.selectedCuidIdx]?.cuid;

    // fetch period data when mount
    if (cuid)
      debouncedFetchPeriodData({
        endpoint: '/forecast/period',
        fetch: post,
        options: {
          body: JSON.stringify({
            siteIds: selectedSiteIds,
            cuid,
          }),
        },
      });
  }, [
    selectedSiteIds,
    forecastEntityParams.selectedCuidIdx,
    quarterData,
    cuidList,
    debouncedFetchPeriodData,
  ]);

  // Checkbox Related Methods
  const [checkboxesIsChecked, setCheckboxesIsChecked] = useState({
    [ForecastPageChartType.ForecastChart]: !forecastChartDisabled,
    [ForecastPageChartType.ErrorRateChart]: !errorRateChartDisabled,
    [ForecastPageChartType.IncomRateChart]: !incomeRateChartDisabled,
  });

  const forecastChartIsActiveCombineWithCheckbox = useMemo(
    () =>
      forecastChartDisabled
        ? false
        : checkboxesIsChecked[ForecastPageChartType.ForecastChart],
    [forecastChartDisabled, checkboxesIsChecked]
  );
  const errorRateChartIsActiveCombineWithCheckbox = useMemo(
    () =>
      errorRateChartDisabled
        ? false
        : checkboxesIsChecked[ForecastPageChartType.ErrorRateChart],
    [errorRateChartDisabled, checkboxesIsChecked]
  );
  const incomeRateChartIsActiveCombineWithCheckbox = useMemo(
    () =>
      incomeRateChartDisabled
        ? false
        : checkboxesIsChecked[ForecastPageChartType.IncomRateChart],
    [incomeRateChartDisabled, checkboxesIsChecked]
  );

  // Forecast Chart Summary Related Methods
  const forecastMeasurementSum = useMemo(() => {
    const length = forecastData?.length;

    if (!forecastChartIsActiveCombineWithCheckbox || !length)
      return (0.0).toFixed(1);

    return forecastData
      ?.reduce(
        (acc, cur) => (acc += cur[ForecastMeasurementType.Measurement] ?? 0),
        0
      )
      .toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
  }, [
    forecastData,
    checkboxesIsChecked[ForecastPageChartType.ForecastChart],
    forecastChartDisabled,
    forecastData,
  ]);

  const errorRateEverage = useMemo(() => {
    const length = errorRateData.filter(
      (data) => data[ForecastMeasurementType.PredictionErrorRate] !== null
    )?.length;
    if (!errorRateChartIsActiveCombineWithCheckbox || !length)
      return (0.0).toFixed(1);

    const sum = errorRateData?.reduce(
      (acc, cur) =>
        (acc += cur[ForecastMeasurementType.PredictionErrorRate] ?? 0),
      0
    );

    return ((sum ?? 0) / length).toFixed(1);
  }, [
    errorRateData,
    checkboxesIsChecked[ForecastPageChartType.ErrorRateChart],
    errorRateChartDisabled,
    errorRateData,
  ]);

  const incomeSum = useMemo(() => {
    const length = incomeRateData?.length;

    if (!incomeRateChartIsActiveCombineWithCheckbox || !length)
      return (0.0).toFixed();

    return incomeRateData
      ?.reduce(
        (acc, cur) => (acc += cur[ForecastMeasurementType.SettlementUnitPrice]),
        0
      )
      .toLocaleString(undefined);
  }, [
    incomeRateData,
    checkboxesIsChecked[ForecastPageChartType.IncomRateChart],
    incomeRateChartDisabled,
    incomeRateData,
  ]);

  // Submit Related Methods
  const onClickSearchButton = () => {
    fetchForecastDataWrapper();
    setShowForecastAlert(false);
  };

  const fetchForecastDataWrapper = useCallback(() => {
    const cuid = cuidList[forecastEntityParams.selectedCuidIdx]?.cuid;

    if (cuid) {
      switch (timePeriod) {
        case TimePeriod.Hourly:
        case TimePeriod.Daily:
          debouncedFetchForecastData({
            endpoint: `/forecast/${timePeriod}`,
            fetch: post,
            options: {
              body: JSON.stringify({
                startDate: format(forecastEntityParams.startDate, 'yyyy-MM-dd'),
                endDate: format(forecastEntityParams.endDate, 'yyyy-MM-dd'),
                siteIds: selectedSiteIds,
                cuid,
              }),
            },
          });
          break;
        case TimePeriod.Monthly:
          debouncedFetchForecastData({
            endpoint: '/forecast/monthly',
            fetch: post,
            options: {
              body: JSON.stringify({
                startYm:
                  forecastEntityParams.monthlyYearStart.split(
                    t('forecast.year')
                  )[0] +
                  '-' +
                  forecastEntityParams.monthlyStart.split(
                    t('forecast.month')
                  )[0],
                endYm:
                  forecastEntityParams.monthlyYearEnd.split(
                    t('forecast.year')
                  )[0] +
                  '-' +
                  forecastEntityParams.monthlyEnd.split(t('forecast.month'))[0],
                siteIds: selectedSiteIds,
                cuid,
              }),
            },
          });
          break;
        case TimePeriod.Yearly:
          debouncedFetchForecastData({
            endpoint: '/forecast/yearly',
            fetch: post,
            options: {
              body: JSON.stringify({
                startYear: forecastEntityParams.yearlyStart.split(
                  t('forecast.year')
                )[0],
                endYear: forecastEntityParams.yearlyEnd.split(
                  t('forecast.year')
                )[0],
                siteIds: selectedSiteIds,
                cuid,
              }),
            },
          });
          break;
      }
    }
  }, [
    selectedSiteIds,
    forecastEntityParams,
    timePeriod,
    cuidList,
    debouncedFetchForecastData,
  ]);

  // Mock Data Related Methods
  const getDataOrMockData = useCallback(
    (
      data: typeof forecastData | typeof errorRateData | typeof incomeRateData
    ) =>
      !data?.length
        ? dataGenerator(
            forecastEntityParams.startDate,
            forecastEntityParams.endDate
          )
        : data,
    [forecastData, errorRateData, incomeRateData, timePeriod]
  );

  // Cuid Dropdown Related Methods
  const onCuidSelected = useCallback(
    (item: { cuidName: string; cuid: string }) => {
      const selectedCuid = cuidList.findIndex(
        (cuid: Pick<Cuid, 'cuid' | 'cuidName'>) => cuid.cuid === item.cuid
      );
      if (selectedCuid >= 0) {
        setForecastEntityParams({
          ...forecastEntityParams,
          selectedCuidIdx: selectedCuid,
        });
      }
    },
    [cuidList, forecastEntityParams]
  );

  const onClickDownloadButton = async () => {
    const response = await get<{ data: string }>(
      '/forecast/manual-submission-sample'
    );
    if (response.status === 'error') {
      return;
    }

    // pre-signed url
    const { data: preSignedUrl } = response;

    // download file
    const fileResponse = await fetch(preSignedUrl, { method: 'GET' });
    const blob = await fileResponse.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '예측발전량제출예시.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const debouncedOnclickDownloadButton = debounce(onClickDownloadButton, 300);

  const debouncedOnclickChartDetailDownloadButton = debounce(async () => {
    // fetch
    const cuid = cuidList[forecastEntityParams.selectedCuidIdx].cuid;
    if (!cuid) throw new Error('Cuid is not selected');

    let s;
    let e;
    let param;
    switch (timePeriod) {
      case TimePeriod.Hourly:
        s = format(forecastEntityParams.startDate, 'yyyy-MM-dd');
        e = format(forecastEntityParams.endDate, 'yyyy-MM-dd');
        param = {
          fetchPeriod: 'hourly',
          start: s,
          end: e,
          siteIds: selectedSiteIds,
          cuid: cuid,
        };
        break;
      case TimePeriod.Daily:
        s = format(forecastEntityParams.startDate, 'yyyy-MM-dd');
        e = format(forecastEntityParams.endDate, 'yyyy-MM-dd');
        param = {
          fetchPeriod: 'daily',
          start: s,
          end: e,
          siteIds: selectedSiteIds,
          cuid: cuid,
        };
        break;
      case TimePeriod.Monthly:
        s =
          forecastEntityParams.monthlyYearStart.split(t('forecast.year'))[0] +
          '-' +
          forecastEntityParams.monthlyStart.split(t('forecast.month'))[0];
        e =
          forecastEntityParams.monthlyYearEnd.split(t('forecast.year'))[0] +
          '-' +
          forecastEntityParams.monthlyEnd.split(t('forecast.month'))[0];
        param = {
          fetchPeriod: 'monthly',
          start: s,
          end: e,
          siteIds: selectedSiteIds,
          cuid: cuid,
        };

        break;
      case TimePeriod.Yearly:
        s = forecastEntityParams.yearlyStart.split(t('forecast.year'))[0];
        e = forecastEntityParams.yearlyEnd.split(t('forecast.year'))[0];
        param = {
          fetchPeriod: 'yearly',
          start: s,
          end: e,
          siteIds: selectedSiteIds,
          cuid: cuid,
        };
        break;
    }

    const response = await post<{ data: string }>('/forecast/chart-detail', {
      body: JSON.stringify(param),
    });
    const fileName = `상세내역 (${s} ~ ${e})`;

    if (response.status === 'error') {
      setShowForecastAlert(true);
      return;
    }

    // pres-signed url
    const { data: preSignedUrl } = response;

    // download file
    const fileResponse = await fetch(preSignedUrl, { method: 'GET' });
    const blob = await fileResponse.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, 300);

  return (
    <SideLayout>
      <div
        data-testid="forecast-page"
        className="flex h-screen flex-col whitespace-nowrap"
      >
        <header className="flex min-h-[80px] items-center bg-light-3">
          <HeaderTitle
            text={t('forecast.prediction', { context: 'header' })}
            className="ml-[50px]"
          ></HeaderTitle>
          {/* spacer */}
          <div className="flex-grow"></div>

          {/* forecast header information */}
          <div className="flex">
            {/* KPX */}
            <div className="flex items-center">
              {/* divider */}
              <div className="h-[30px] w-[1px] bg-[#CDD5D5]"></div>
              <div className="ml-[40px] flex flex-col">
                <p className="pop-medium-3 leading-[23px] text-dark-10">
                  {t('forecast.submission', { context: 'header' })}
                </p>
                <div className="flex">
                  <KpxSubmission
                    text={t('forecast.submission', { context: 'first' })}
                    status={
                      forecastDayAheadData.submissionFirst
                        ? KpxSubmissionStatusType.Submitted
                        : KpxSubmissionStatusType.Unknown
                    }
                  ></KpxSubmission>
                  <KpxSubmission
                    text={t('forecast.submission', { context: 'second' })}
                    status={
                      forecastDayAheadData.submissionSecond
                        ? KpxSubmissionStatusType.Submitted
                        : KpxSubmissionStatusType.Unknown
                    }
                    className="ml-[20px]"
                  ></KpxSubmission>
                  <p
                    className="skip-ink-none pop-13-semibold mb-[2px] ml-[20px] cursor-pointer self-end text-blue-main underline underline-offset-2px"
                    onClick={debouncedOnclickDownloadButton}
                    style={{
                      visibility: 'hidden', // visible, hidden
                    }}
                  >
                    {t('forecast.submission', { context: 'download' })}
                  </p>
                </div>
              </div>
            </div>
            {/* 발전 */}
            <div className="ml-[40px] flex items-center">
              {/* divider */}
              {/* <div className="h-[30px] w-[1px] bg-[#CDD5D5]"></div> */}
              {/* <div className="ml-[40px] flex flex-col"> */}
              {/* <p className="pop-medium-3 leading-[23px] tracking-[-0.5px] text-dark-10">
                  {t('forecast.generation', { context: 'time' })}
                </p> */}
              {/* <Generation
                  text={
                    forecastDayAheadData.optimeValid
                      ? forecastDayAheadData.optimeValid + ' h'
                      : '-'
                  }
                  status={
                    forecastDayAheadDiffData.optimeValidDiff
                      ? Math.abs(
                          forecastDayAheadDiffData.optimeValidDiff
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        }) + ' h'
                      : ''
                  }
                  isPlus={forecastDayAheadDiffData.optimeValidDiff > 0}
                ></Generation> */}
              {/* </div> */}
            </div>
            {/* 발전 */}
            <div className="ml-[40px] mr-[50px] flex items-center">
              {/* divider */}
              <div className="h-[30px] w-[1px] bg-[#CDD5D5]"></div>
              <div className="ml-[40px] flex flex-col">
                <p className="pop-medium-3 leading-[23px] tracking-[-0.5px] text-dark-10">
                  {t('forecast.generation', { context: 'sum' })}
                </p>
                <Generation
                  text={
                    forecastDayAheadData.powerPred
                      ? forecastDayAheadData.powerPred + ' MWh'
                      : '-'
                  }
                  status={
                    forecastDayAheadDiffData.powerPredDiff
                      ? Math.abs(
                          forecastDayAheadDiffData.powerPredDiff
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        }) + ' MWh'
                      : ''
                  }
                  isPlus={forecastDayAheadDiffData.powerPredDiff > 0}
                ></Generation>
              </div>
            </div>
          </div>
        </header>
        {/* sections */}
        <div className="min-w-[1200px] flex-1 bg-[#F3F6F8] px-[50px] pb-[70px] pt-[30px] [&>*]:mx-auto [&>*]:max-w-[1100px]">
          <section className="flex items-center">
            {cuidListLoading ? (
              <DropdownSpinner className="[&>li]:h-[40px] [&>li]:w-[240px]" />
            ) : (
              <>
                <ForecastCuidDropdown
                  testId="cuid-dropdown-button"
                  itemTestId="cuid-dropdown-items"
                  items={
                    cuidList.length > 0
                      ? cuidList
                      : [
                          {
                            cuidName: t('addAccountModal.defaultRole'),
                            cuid: '',
                          },
                        ]
                  }
                  selectedItem={
                    cuidList[forecastEntityParams.selectedCuidIdx]?.cuidName ??
                    t('addAccountModal.defaultRole')
                  }
                  className="mr-[10px] min-w-[181px] text-dark-3 [&>div:first-child>button]:noto-medium-4 [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[40px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:pb-[13px] [&>div:first-child>button]:pl-[21px] [&>div:first-child>button]:pr-[15px] [&>div:first-child>button]:pt-[15px] [&>div:first-child>button]:ring-2 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-dark-10 [&[data-headlessui-state=open]>div:first-child>button]:bg-light-2 [&[data-headlessui-state=open]>div:first-child>button]:ring-teal-dark"
                  onSelected={onCuidSelected}
                />
                <ForecastMultiSelectDropdown
                  testId="cuid-multi-select-dropdown-button"
                  itemTestId="cuid-multi-select-dropdown-items"
                  items={
                    siteIds.length > 0
                      ? siteIds
                      : [
                          {
                            siteId: '',
                            siteName: t('addAccountModal.defaultRole'),
                          },
                        ]
                  }
                  selectedItem={
                    isItemsChecked.filter((value) => value).length > 0 &&
                    siteIds.length > 0
                      ? siteIds[isItemsChecked.findIndex((value) => value)]
                          .siteName
                      : t('addAccountModal.defaultRole')
                  }
                  className="min-w-[240px] text-dark-3 [&>div:first-child>button]:noto-medium-4 [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[40px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:pb-[13px] [&>div:first-child>button]:pl-[21px] [&>div:first-child>button]:pr-[15px] [&>div:first-child>button]:pt-[15px] [&>div:first-child>button]:ring-2 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-dark-10 [&[data-headlessui-state=open]>div:first-child>button]:bg-light-2 [&[data-headlessui-state=open]>div:first-child>button]:ring-teal-dark"
                  setIsItemsChecked={setIsItemsChecked}
                  isItemsChecked={isItemsChecked}
                ></ForecastMultiSelectDropdown>
              </>
            )}
            <ForecastPhaseSummary
              data={forecastPeriodData}
              className="ml-auto"
            />
          </section>
          <section className="mt-[30px] flex justify-between">
            <ForecastSummaryCard
              title="당일 요약"
              data={
                todayFetchedData?.data && todayFetchedData.data.length > 0
                  ? (todayFetchedData?.data[0] as ForecastSummaryCardData)
                  : undefined
              }
            />
            <ForecastSummaryCard
              title="당월 요약"
              data={
                currentMonthFetchedData?.data &&
                currentMonthFetchedData.data.length > 0
                  ? (currentMonthFetchedData
                      ?.data[0] as ForecastSummaryCardData)
                  : undefined
              }
            />
          </section>

          {/* divider */}
          <div className="mt-10 h-[0.5px] w-full bg-[#D1DBDA]"></div>
          <section className="mt-[26px]">
            <div className="flex h-[40px] items-end justify-between">
              <p className="pop-semibold-4 pb-[3px] leading-[23px] tracking-[-0.5px] text-dark-2">
                {t('forecast.summary')}
              </p>
              {/* Forecast Alert Message */}
              {showForecastAlert && (
                <ForecastAlert
                  testId="forecast-alert"
                  message="다운로드에 실패했습니다"
                />
              )}
            </div>
            <div className="mt-[17px] flex w-full items-center justify-between">
              <div data-testid="forecast-test" className="flex items-center">
                <Dropdown
                  testId="chart-dropdown-button"
                  itemTestId="chart-dropdown-items"
                  items={Object.values(TimePeriod).map((value) =>
                    t(`forecast.timePeriod.${value}`)
                  )}
                  selectedItem={t(`forecast.timePeriod.${timePeriod}`)}
                  onSelected={onSelected}
                  className="mt-[2px] w-[140px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-yellow-main [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:bg-light-2 [&[data-headlessui-state=open]>div:first-child>button]:ring-yellow-dark"
                ></Dropdown>
                {/* 실시간, 일간 */}
                {(timePeriod === TimePeriod.Hourly ||
                  timePeriod === TimePeriod.Daily) && (
                  <>
                    <DateSelector
                      testId={
                        timePeriod === TimePeriod.Hourly
                          ? 'forecast-hourly-test-start'
                          : 'forecast-daily-test-start'
                      }
                      date={forecastEntityParams.startDate}
                      onChange={forecastHourlyAndDailyDateOnChange('startDate')}
                      className="ml-[10px] [&>div>div>input]:h-[36px] [&]:w-[150px]"
                      colors="bg-light-1 bg-opacity-100 text-dark-10"
                      inputBase="border-light-6 [&]:pl-[53px]"
                    ></DateSelector>
                    <DateSelector
                      testId={
                        timePeriod === TimePeriod.Hourly
                          ? 'forecast-hourly-test-end'
                          : 'forecast-daily-test-end'
                      }
                      date={forecastEntityParams.endDate}
                      onChange={forecastHourlyAndDailyDateOnChange('endDate')}
                      className="ml-[10px] [&>div>div>input]:h-[36px] [&]:w-[150px]"
                      colors="bg-light-1 bg-opacity-100 text-dark-10"
                      inputBase="border-light-6 [&]:pl-[53px]"
                    ></DateSelector>
                  </>
                )}
                {/* 월간 */}
                {timePeriod === TimePeriod.Monthly && (
                  <>
                    <Dropdown
                      testId="forecast-monthly-year-start"
                      items={years}
                      className="ml-[10px] mt-[2px] w-[120px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                      selectedItem={forecastEntityParams.monthlyYearStart}
                      onSelected={forecastMonthlyDateOnChange(
                        'monthlyYearStart'
                      )}
                    ></Dropdown>
                    <Dropdown
                      testId="forecast-monthly-test-start"
                      items={months}
                      className="ml-[10px] mt-[2px] w-[90px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                      selectedItem={forecastEntityParams.monthlyStart}
                      onSelected={forecastMonthlyDateOnChange('monthlyStart')}
                    ></Dropdown>
                    <span className="noto-11-bold ml-[5px] text-dark-2">~</span>
                    <Dropdown
                      testId="forecast-monthly-year-end"
                      items={years}
                      className="ml-[10px] mt-[2px] w-[120px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                      selectedItem={forecastEntityParams.monthlyYearEnd}
                      onSelected={forecastMonthlyDateOnChange('monthlyYearEnd')}
                    ></Dropdown>
                    <Dropdown
                      testId="forecast-monthly-test-end"
                      items={months}
                      selectedItem={forecastEntityParams.monthlyEnd}
                      onSelected={forecastMonthlyDateOnChange('monthlyEnd')}
                      className="ml-[5px] mt-[2px] w-[90px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                    ></Dropdown>
                  </>
                )}
                {/* 연도별 */}
                {timePeriod === TimePeriod.Yearly && (
                  <>
                    <Dropdown
                      testId="forecast-yearly-start"
                      items={years}
                      selectedItem={forecastEntityParams.yearlyStart}
                      onSelected={forecastYearlyDateOnChange('yearlyStart')}
                      className="ml-[10px] mt-[2px] w-[120px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                    ></Dropdown>
                    <span className="noto-11-bold ml-[5px] text-dark-2">~</span>
                    <Dropdown
                      testId="forecast-yearly-end"
                      items={years}
                      selectedItem={forecastEntityParams.yearlyEnd}
                      onSelected={forecastYearlyDateOnChange('yearlyEnd')}
                      className="ml-[5px] mt-[2px] w-[120px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                    ></Dropdown>
                  </>
                )}
                <SearchButton
                  testId="forecast-search-button"
                  onClick={onClickSearchButton}
                  text={t('forecast.search')}
                  className="ml-[10px] cursor-pointer [&]:shadow-none"
                ></SearchButton>
              </div>
              {/* Button */}
              {forecastChartIsActiveCombineWithCheckbox ||
              errorRateChartIsActiveCombineWithCheckbox ||
              incomeRateChartIsActiveCombineWithCheckbox ? (
                <div className={`flex justify-end`}>
                  <ForecastDownloadButton
                    onClick={debouncedOnclickChartDetailDownloadButton}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>
            {/* Charts */}
            <article className="mt-[20px] w-full rounded-[10px] bg-white shadow-3">
              {
                <div
                  className={`${forecastChartIsActiveCombineWithCheckbox ? '' : 'hidden'}`}
                >
                  {/* Chart Header */}
                  <div
                    data-testid="forecast-chart-header"
                    className="flex items-center justify-between pl-[28px] pr-[33px] pt-[27px]"
                  >
                    <p className="noto-15-medium mb-5 text-dark-3">
                      {t('forecast.generation', { context: 'header' })}
                    </p>
                    <ForecastMeasurementSummary
                      text={t('forecast.generation', {
                        context: 'summary',
                        value: forecastMeasurementSum,
                      })}
                      className="border-yellow-light text-dark-6 [&>div:first-child]:bg-yellow-main"
                    ></ForecastMeasurementSummary>
                  </div>
                  {/* Chart */}
                  <div className="h-[370px]">
                    <ForecastLineChart
                      showLegend={true}
                      data={getDataOrMockData(forecastData)}
                    ></ForecastLineChart>
                  </div>
                </div>
              }
              {
                <div
                  className={`${errorRateChartIsActiveCombineWithCheckbox ? '' : 'hidden'} pb-[30px]`}
                >
                  {/* Chart Header */}
                  <div
                    data-testid="error-chart-header"
                    className="flex items-end justify-between pl-[28px] pr-[33px] pt-[33px]"
                  >
                    <p className="noto-15-medium pb-[14px]">{`${t('forecast.percentageError')} (${t('forecast.percentageError', { context: 'unit' })})`}</p>
                    <div className="flex flex-col items-end">
                      <ForecastMeasurementSummary
                        text={t('forecast.percentageError', {
                          context: 'summary',
                          value: errorRateEverage,
                        })}
                        className="border-red-light [&>div:first-child]:bg-red-light"
                      />
                      <ForecastMeasurementSummary
                        text={t('forecast.settlementAmount', {
                          context: 'summary',
                          value: incomeSum,
                        })}
                        className="mt-[15px] border-[#00D087] [&>div:first-child]:bg-[#00D087]"
                      />
                    </div>
                  </div>
                  {/* Chart */}
                  <div className="h-[342px]">
                    <ForecastLineChart
                      data={getDataOrMockData(errorRateData)}
                    ></ForecastLineChart>
                  </div>
                </div>
              }
            </article>
            {forecastChartIsActiveCombineWithCheckbox ||
            errorRateChartIsActiveCombineWithCheckbox ||
            incomeRateChartIsActiveCombineWithCheckbox ? (
              <></>
            ) : (
              <article className="mt-[90px] flex flex-col items-center justify-center">
                <SVGImage
                  height={20}
                  width={20}
                  pathFill="#7E8993"
                  name="warning"
                ></SVGImage>
                <p className="noto-14-normal mt-[5px] text-dark-10">
                  {t('forecast.dataNotExist')}
                </p>
              </article>
            )}
          </section>
        </div>
      </div>
    </SideLayout>
  );
}

export default ForecastPage;
